import * as XLSX from 'xlsx';

export type PriceListSheetMatrix = (string | number | boolean | null | undefined)[][];

function safeExcelSheetName(name: string): string {
  const cleaned = String(name || 'Sheet1')
    .replace(/[:\\/?*[\]]/g, ' ')
    .trim();
  const base = cleaned || 'Sheet1';
  return base.length > 31 ? base.slice(0, 31) : base;
}

/**
 * Reads the first worksheet from .xlsx / .xls / .csv into a rectangular matrix (header row + data).
 */
export async function parseExcelFileToMatrix(file: File): Promise<{
  matrix: PriceListSheetMatrix;
  sheetName: string;
}> {
  const buf = await file.arrayBuffer();
  const workbook = XLSX.read(buf, { type: 'array', cellDates: true });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    return { matrix: [], sheetName: 'Sheet1' };
  }
  const sheet = workbook.Sheets[sheetName];
  const matrix = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: '',
    raw: false,
  }) as PriceListSheetMatrix;

  return { matrix, sheetName };
}

/**
 * Writes matrix back to .xlsx with the same shape the backend expects from uploads.
 */
export function matrixToXlsxFile(matrix: PriceListSheetMatrix, sheetName: string, fileName: string): File {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(matrix);
  XLSX.utils.book_append_sheet(wb, ws, safeExcelSheetName(sheetName));
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const name = /\.xlsx$/i.test(fileName) ? fileName : `${fileName.replace(/\.(xls|csv)$/i, '')}.xlsx`;
  return new File([out], name, {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

export function normalizeMatrixRows(matrix: PriceListSheetMatrix): PriceListSheetMatrix {
  if (!matrix.length) return matrix;
  const colCount = Math.max(...matrix.map((row) => (row ? row.length : 0)));
  return matrix.map((row) => {
    const r = row ? [...row] : [];
    while (r.length < colCount) r.push('');
    return r;
  });
}

/** Normalise a header cell for comparisons (NBSP, case, spacing). */
function normalizeHeaderText(headerCell: unknown): string {
  return String(headerCell ?? '')
    .normalize('NFKC')
    .replace(/\u00a0/g, ' ')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/\s*\([^)]*\)\s*$/g, '')
    .replace(/\*+$/g, '')
    .trim();
}

/**
 * Serial / row-index style columns must never be treated as the editable price column.
 * Templates sometimes put Sr.No as the last column; the old "last column" fallback picked it by mistake.
 */
function cellLooksSerialOrIndexColumn(headerCell: unknown): boolean {
  const h = normalizeHeaderText(headerCell);
  if (!h) return false;

  // Common serial headers: Sr.No, S.No, Sl.No, Serial, Row, Line, #
  if (/^(sr\.?\s*no|s\.?\s*no|sl\.?\s*no)$/i.test(h)) return true;
  if (/^serial(\s*(no|number|#))?$/i.test(h)) return true;
  if (/^(row|line)(\s*(no|number|#))?$/i.test(h)) return true;
  if (/^#+$/.test(h)) return true;
  if (/^no\.?\s*$/i.test(h)) return true;

  const compact = h.replace(/[\s._-]/g, '');
  if (compact === 'srno' || compact === 'sno' || compact === 'slno' || compact === 'serialno') return true;

  // "Sr No" without dot
  if (/^sr\s+no$/i.test(h)) return true;

  return false;
}

export function cellLooksPriceColumn(headerCell: unknown): boolean {
  const h = normalizeHeaderText(headerCell);
  if (!h) return false;
  if (cellLooksSerialOrIndexColumn(headerCell)) return false;
  // Ignore title-like cells such as "Shehzad Price List".
  if (/\bprice\s*list\b/.test(h) || h.includes('pricelist')) return false;
  if (h.includes('default')) return false;
  if (h.includes('price')) return true;
  const compact = h.replace(/[\s_-]/g, '');
  return compact === 'plprice';
}

/** Resolve one editable price column index. */
export function resolveEditablePriceColumnIndex(headerRow: unknown[]): number | null {
  if (!headerRow.length) return null;
  const matches: number[] = [];

  headerRow.forEach((cell, index) => {
    if (cellLooksPriceColumn(cell)) matches.push(index);
  });

  if (matches.length === 1) return matches[0];
  if (matches.length > 1) return matches[matches.length - 1];

  // No explicit "price" in headers: pick the rightmost column that is clearly not a serial/index column.
  for (let i = headerRow.length - 1; i >= 0; i--) {
    if (!cellLooksSerialOrIndexColumn(headerRow[i])) {
      return i;
    }
  }

  return headerRow.length > 1 ? headerRow.length - 1 : 0;
}
